import { useCallback, useEffect, useMemo, useState } from "react";
import { getApiErrorMessage } from "../../../shared/lib";
import {
  BroadcastNotificationsResult,
  deleteBroadcastDraft,
  getBroadcastNotifications,
  GetBroadcastNotificationsParams,
  saveBroadcastDraft,
  sendBroadcastNotification,
  updateBroadcastDraft,
} from "../../../shared/api/broadcastNotifications";
import { BroadcastNotification, ComposeBroadcastPayload } from "../types";

type UseNotificationsState = BroadcastNotificationsResult & {
  isLoading: boolean;
  error: string | null;
};

const notificationsCache = new Map<string, BroadcastNotificationsResult>();

export const useNotifications = (params: GetBroadcastNotificationsParams) => {
  const [state, setState] = useState<UseNotificationsState>({
    items: [],
    meta: undefined,
    isLoading: true,
    error: null,
  });
  const [isSending, setIsSending] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const requestKey = JSON.stringify(params);
  const requestParams = useMemo(() => params, [requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const cached = notificationsCache.get(requestKey);
      setState((prev) => ({
        items: cached?.items ?? prev.items,
        meta: cached?.meta ?? prev.meta,
        isLoading: !cached,
        error: null,
      }));

      try {
        const result = await getBroadcastNotifications(requestParams);
        if (!isMounted) return;
        notificationsCache.set(requestKey, result);
        setState({ ...result, isLoading: false, error: null });
      } catch (error) {
        if (!isMounted) return;
        setState({
          items: [],
          meta: undefined,
          isLoading: false,
          error: getApiErrorMessage(error, "Unable to load notifications"),
        });
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [refreshCount, requestKey, requestParams]);

  const sendBroadcast = useCallback(
    async (payload: ComposeBroadcastPayload): Promise<BroadcastNotification> => {
      setIsSending(true);
      try {
        const record = await sendBroadcastNotification(payload);
        notificationsCache.clear();
        return record;
      } finally {
        setIsSending(false);
      }
    },
    [],
  );

  const saveDraft = useCallback(
    async (payload: ComposeBroadcastPayload): Promise<BroadcastNotification> => {
      setIsSending(true);
      try {
        const record = await saveBroadcastDraft(payload);
        notificationsCache.clear();
        return record;
      } finally {
        setIsSending(false);
      }
    },
    [],
  );

  const updateDraft = useCallback(
    async (
      id: string,
      payload: ComposeBroadcastPayload,
      send: boolean,
    ): Promise<BroadcastNotification> => {
      setIsSending(true);
      try {
        const record = await updateBroadcastDraft(id, payload, send);
        notificationsCache.clear();
        return record;
      } finally {
        setIsSending(false);
      }
    },
    [],
  );

  const deleteDraft = useCallback(async (id: string): Promise<void> => {
    await deleteBroadcastDraft(id);
    notificationsCache.clear();
  }, []);

  return {
    ...state,
    isSending,
    sendBroadcast,
    saveDraft,
    updateDraft,
    deleteDraft,
    refetch: () => setRefreshCount((prev) => prev + 1),
  };
};
